import dayjs from 'dayjs';
import {t} from 'i18next';
import {isEmpty} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {getMonthOperators, QBOperators} from '../../../../utils/queryBuilder';
import {
  FieldType,
  FieldUIType,
  FieldUITypeMappingObj,
  IFieldModal,
} from '../../field-management/custom-field/constant';
import {FieldCategoryType} from '../../field-management/custom-field/FieldView';
import {
  getDateTimeFieldValue,
  isDateFieldApplyDisabled,
  isDateFieldType,
  isFileFieldApplyDisabled,
  isGeneralFieldApplyDisabled,
} from './query-field-utils';
import QueryFieldDateTime, {
  IQueryFieldDateTimeParams,
} from './QueryFieldDateTime';
import {QueryFieldDrawer} from './QueryFieldDrawer';
import {
  IQueryFieldFileTransferListParams,
  QueryFieldFileTransferList,
} from './QueryFieldFileTransferList';
import {
  IQueryFieldGeneralInputParams,
  QueryFieldGeneralInput,
} from './QueryFieldGeneralInput';
import QueryFieldSelection from './QueryFieldSelection';
import {useQueryField} from './useQueryField';

interface IExtraValue
  extends
    IQueryFieldDateTimeParams,
    IQueryFieldGeneralInputParams,
    IQueryFieldFileTransferListParams {}

export interface IQueryFieldMetadataDrawerValue {
  field?: IFieldModal;
  fieldValue?: {
    fieldValue?: string;
    extraValue?: IExtraValue;
  };
}

interface IQueryFieldMetadataDrawerProps {
  type: FieldCategoryType;
  open: boolean;
  initialValue?: {
    fieldId: number;
    value: string;
    operator: QBOperators;
    hasTransferList?: boolean;
  };
  onCancel: () => void;
  onApply?: (value: IQueryFieldMetadataDrawerValue) => void;
}

const QueryFieldMetadataDrawer: React.FC<IQueryFieldMetadataDrawerProps> = ({
  type,
  initialValue,
  open,
  onCancel,
  onApply,
}) => {
  const [isModal, setIsModal] = useState<boolean>(open);
  const [selectedQueryField, setSelectedQueryField] = useState<IFieldModal>();
  const [selectingQueryField, setSelectingQueryField] = useState<IFieldModal>();
  const [dateTimeValues, setDateTimeValues] =
    useState<IQueryFieldDateTimeParams>();
  const [generalInputValues, setGeneralInputValues] =
    useState<IQueryFieldGeneralInputParams>();
  const [fileTransferListValues, setFileTransferListValues] =
    useState<IQueryFieldFileTransferListParams>();
  const [searchTerm, setSearchTerm] = useState<string>();

  const {loading, fields} = useQueryField(type);
  useEffect(() => {
    setIsModal(open);
  }, [open]);

  const handleSingleDate = useCallback(
    (value: string, fieldUIType?: FieldType) => {
      const today = dayjs().format('YYYY-MM-DD');
      setDateTimeValues({
        operator: QBOperators.isIn, // or actual operator if passed
        startDate: dayjs(value),
        startTime: dayjs(fieldUIType === 'Time' ? `${today} ${value}` : value),
      });
    },
    []
  );

  const handleBetweenOperator = useCallback(
    (value: string, fieldUIType?: FieldType) => {
      const [start, end] = value.split(' TO ');
      const today = dayjs().format('YYYY-MM-DD');

      if (fieldUIType === 'DateTime') {
        setDateTimeValues({
          operator: QBOperators.between,
          startDate: dayjs(start),
          endDate: dayjs(end),
          startTime: dayjs(start),
          endTime: dayjs(end),
        });
      } else if (fieldUIType === 'Date') {
        setDateTimeValues({
          operator: QBOperators.between,
          startDate: dayjs(start),
          endDate: dayjs(end),
        });
      } else if (fieldUIType === 'Time') {
        setDateTimeValues({
          operator: QBOperators.between,
          startTime: dayjs(`${today} ${start}`),
          endTime: dayjs(`${today} ${end}`),
        });
      }
    },
    []
  );

  const handleIsInOperator = useCallback((value: string) => {
    const monthOp = getMonthOperators().find((item) => item.name === value);
    if (monthOp) {
      setDateTimeValues({
        operator: QBOperators.isIn,
        periodType: QBOperators.month,
        periodValue: monthOp.name,
      });
    } else {
      setDateTimeValues({
        operator: QBOperators.isIn,
        periodType: value as QBOperators,
      });
    }
  }, []);

  const handleDateType = useCallback(
    (operator: string, value: string, fieldUIType?: FieldType) => {
      if (operator === QBOperators.isIn) {
        handleIsInOperator(value);
      } else if (operator === QBOperators.between) {
        handleBetweenOperator(value, fieldUIType);
      } else {
        handleSingleDate(value, fieldUIType);
      }
    },
    [handleBetweenOperator, handleIsInOperator, handleSingleDate]
  );

  useEffect(() => {
    if (!initialValue) return;

    const {operator, value, fieldId, hasTransferList = false} = initialValue;
    const getField = fields.find((field) => field.fieldId === fieldId);
    const fieldUIType =
      FieldUITypeMappingObj[selectedQueryField?.fieldTypeId as FieldUIType];

    const isDateType = ['DateTime', 'Date', 'Time'].includes(fieldUIType ?? '');

    if (hasTransferList) {
      setFileTransferListValues({
        operator,
        value,
      });
    } else if (isDateType) {
      handleDateType(operator, value, fieldUIType);
    } else {
      setGeneralInputValues({operator, value});
    }

    setSelectedQueryField(getField);
    setSelectingQueryField(getField);
  }, [
    fields,
    handleDateType,
    initialValue,
    selectedQueryField,
    selectedQueryField?.fieldTypeId,
  ]);

  const filterFields = fields.filter(
    (item) =>
      item.fieldCategory === type &&
      item.fieldLabel.toLowerCase().includes((searchTerm ?? '').toLowerCase())
  );

  const onCancelClicked = useCallback(() => {
    setIsModal(false);
    onCancel?.();
  }, [onCancel]);

  const onApplyClicked = useCallback(() => {
    const fieldUIType =
      FieldUITypeMappingObj[selectedQueryField?.fieldTypeId as FieldUIType];

    if (!selectedQueryField) {
      setSelectedQueryField(selectingQueryField);
      return;
    }

    setIsModal(false);

    let fieldValue, extraValue;

    if (selectedQueryField?.hasTransferList ?? false) {
      fieldValue = fileTransferListValues?.value ?? '*';
      extraValue = fileTransferListValues;
    } else {
      fieldValue = isDateFieldType(fieldUIType)
        ? getDateTimeFieldValue(dateTimeValues, fieldUIType)
        : generalInputValues?.value;

      extraValue = isDateFieldType(fieldUIType)
        ? dateTimeValues
        : generalInputValues;
    }

    if (
      selectedQueryField.fieldName &&
      selectedQueryField.fieldName.toLowerCase() === 'documentid' &&
      extraValue &&
      (extraValue.operator === QBOperators.is ||
        extraValue.operator === QBOperators.isNot ||
        extraValue.operator === QBOperators.contains ||
        extraValue.operator === QBOperators.doesNotContain)
    ) {
      fieldValue = joinWithOr(fieldValue ?? '');
      const newExtraValue: IQueryFieldFileTransferListParams = {
        operator: extraValue.operator,
        value: fieldValue,
      };
      extraValue = newExtraValue;
    }
    onApply?.({
      field: selectedQueryField,
      fieldValue: {fieldValue, extraValue},
    });
  }, [
    dateTimeValues,
    fileTransferListValues,
    generalInputValues,
    onApply,
    selectedQueryField,
    selectingQueryField,
  ]);

  const joinWithOr = (input: string): string => {
    if (!input) return input;
    const ids = input.trim()
      .split(/\s+OR\s+/i)
      .flatMap((segment) => segment.trim().split(/\s+/))
      .map((id) => id.trim())
      .filter(Boolean);
    return ids.join(" OR ");
    
  };

  const onBackClicked = useCallback(() => {
    setSelectedQueryField(undefined);
    setSelectingQueryField(undefined);
    setGeneralInputValues(undefined);
    setDateTimeValues(undefined);
    setFileTransferListValues(undefined);
  }, []);

  const onSearch = useCallback((search?: string) => {
    setSearchTerm(search);
  }, []);

  const isApplyDisabled = useMemo(() => {
    if (!selectedQueryField) {
      return isEmpty(selectingQueryField);
    }

    if (selectedQueryField?.hasTransferList ?? false) {
      return isFileFieldApplyDisabled(fileTransferListValues);
    }

    const fieldUIType =
      FieldUITypeMappingObj[selectedQueryField.fieldTypeId as FieldUIType];

    if (isDateFieldType(fieldUIType)) {
      return isDateFieldApplyDisabled(fieldUIType, dateTimeValues);
    }

    return isGeneralFieldApplyDisabled(generalInputValues);
  }, [
    dateTimeValues,
    generalInputValues,
    selectedQueryField,
    selectingQueryField,
    fileTransferListValues,
  ]);

  const headerTitle = useMemo(() => {
    if (selectedQueryField) {
      return selectedQueryField.fieldLabel;
    }

    if (type === 'STANDARD_METADATA') {
      return t('indexSearchPage.standardDrawer.standard');
    }

    return t('indexSearchPage.customDrawer.custom');
  }, [selectedQueryField, type]);

  const renderQueryFieldSelection = useCallback(
    () => (
      <QueryFieldSelection
        loading={loading}
        fields={filterFields}
        onSelectOption={setSelectingQueryField}
      />
    ),
    [filterFields, loading]
  );

  const renderQueryFieldForm = useCallback(() => {
    const fieldUIType =
      FieldUITypeMappingObj[selectedQueryField?.fieldTypeId as FieldUIType];
    if (
      fieldUIType === 'DateTime' ||
      fieldUIType === 'Date' ||
      fieldUIType === 'Time'
    ) {
      return (
        <QueryFieldDateTime
          selectedField={selectedQueryField}
          initialInputValue={dateTimeValues}
          onChangeFieldParams={(values) => setDateTimeValues(values)}
        />
      );
    }

    return (
      <QueryFieldGeneralInput
        selectedField={selectedQueryField}
        initialInputValue={generalInputValues}
        onChangeFieldParams={(values) => setGeneralInputValues(values)}
        componentName={
          type === 'STANDARD_METADATA'
            ? t('queryBuilder.standardMetadataField')
            : t('queryBuilder.customMetadataField')
        }
      />
    );
  }, [dateTimeValues, generalInputValues, selectedQueryField]);

  const renderQueryFieldTransferList = useCallback(
    () => (
      <QueryFieldFileTransferList
        selectedField={selectedQueryField}
        initialInputValue={fileTransferListValues}
        onChangeFieldParams={setFileTransferListValues}
      />
    ),
    [selectedQueryField, fileTransferListValues]
  );

  const renderQueryFieldParam = useCallback(() => {
    if (selectedQueryField?.hasTransferList ?? false) {
      return renderQueryFieldTransferList();
    }

    return selectedQueryField
      ? renderQueryFieldForm()
      : renderQueryFieldSelection();
  }, [
    renderQueryFieldForm,
    renderQueryFieldSelection,
    renderQueryFieldTransferList,
    selectedQueryField,
  ]);

  return (
    <QueryFieldDrawer
      open={isModal}
      headerProps={{
        title: headerTitle,
        componentName:
          type === 'STANDARD_METADATA'
            ? t('queryBuilder.standardMetadataField')
            : t('queryBuilder.customMetadataField'),
        isApplyDisabled,
        isBackEnabled: !isEmpty(selectedQueryField) && isEmpty(initialValue),
        onBackClicked,
        onCancelClicked,
        onApplyClicked,
      }}
      searchProps={
        selectedQueryField || loading
          ? undefined
          : {
              title:
                type === 'STANDARD_METADATA'
                  ? t('indexSearchPage.standardDrawer.searchTitle')
                  : t('indexSearchPage.customDrawer.searchTitle'),
              onSearch,
            }
      }
    >
      {renderQueryFieldParam()}
    </QueryFieldDrawer>
  );
};

export {QueryFieldMetadataDrawer};
